import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, ArrowUpRight, ShoppingBag, LayoutGrid, PenLine, Loader2 } from "lucide-react";
import { SERVICES, PRODUCTS, BLOG_URL } from "@/data/content";

const decode = (t) => new DOMParser().parseFromString(t, "text/html").documentElement.textContent;

const SUGGESTIONS = ["HR", "Payroll", "Branding", "Recruitment", "Compliance", "Digital", "Strategy"];

const FindService = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) {
      window.__lenis?.stop();
      document.body.style.overflow = "hidden";
    } else {
      window.__lenis?.start();
      document.body.style.overflow = "";
      setQ("");
      setPosts([]);
    }
  }, [open]);

  useEffect(() => {
    if (!q.trim()) {
      setPosts([]);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const r = await fetch(
          `https://public-api.wordpress.com/wp/v2/sites/hrmexpress.wordpress.com/posts?search=${encodeURIComponent(q)}&per_page=5&_fields=link,title`
        );
        const data = await r.json();
        setPosts(Array.isArray(data) ? data : []);
      } catch {
        setPosts([]);
      }
      setLoading(false);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const ql = q.trim().toLowerCase();
  const svcResults = ql
    ? SERVICES.filter((s) =>
        [s.title, s.line, s.tag, ...s.groups.flatMap((g) => [g.name, ...g.items.map((i) => i.n)])]
          .join(" ")
          .toLowerCase()
          .includes(ql)
      )
    : [];
  const prodResults = ql
    ? PRODUCTS.filter((p) => `${p.name} ${p.includes} ${p.shop}`.toLowerCase().includes(ql))
    : [];

  const goService = (slug) => {
    onClose();
    navigate(`/services/${slug}`);
  };
  const goStore = () => {
    onClose();
    navigate("/store");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="find-service-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[70] bg-[#FAF5EC]/97 backdrop-blur-md overflow-y-auto"
        >
          <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-24 pb-16">
            <div className="flex items-center justify-between mb-8">
              <p className="text-xs uppercase tracking-[0.3em] font-mono text-kiki-gold font-semibold">Find my service</p>
              <button
                data-testid="find-service-close"
                onClick={onClose}
                aria-label="Close search"
                className="w-10 h-10 rounded-full border border-kiki-line bg-white flex items-center justify-center text-kiki-ink hover:text-kiki-gold hover:border-kiki-gold/40 transition-colors duration-300"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-kiki-muted" />
              <input
                data-testid="find-service-input"
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search services, products, articles…"
                className="w-full bg-white border border-kiki-line focus:border-kiki-gold/60 rounded-full pl-14 pr-6 py-5 text-lg text-kiki-ink placeholder:text-kiki-muted/70 outline-none transition-colors duration-300 font-light shadow-lg shadow-kiki-ink/5"
              />
            </div>

            {!ql && (
              <div data-testid="find-service-suggestions" className="mt-8">
                <p className="font-display text-2xl sm:text-3xl font-medium text-kiki-ink">What are you looking for?</p>
                <p className="text-sm text-kiki-muted font-light mt-2">
                  Search across all Kiki services, digital products, and HRM Express articles at once.
                </p>
                <div className="flex flex-wrap gap-2.5 mt-5">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={s}
                      data-testid={`suggestion-chip-${i}`}
                      onClick={() => setQ(s)}
                      className="text-sm font-outfit font-medium bg-kiki-surface hover:bg-kiki-gold hover:text-[#FAF5EC] text-kiki-gold rounded-full px-5 py-2.5 transition-colors duration-300"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {ql && (
              <div data-testid="find-service-results" className="mt-10 space-y-10">
                <div>
                  <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-mono text-kiki-ink font-semibold mb-4">
                    <LayoutGrid className="w-3.5 h-3.5 text-kiki-gold" /> Services
                  </p>
                  {svcResults.length === 0 ? (
                    <p className="text-sm text-kiki-muted font-light">No matching services.</p>
                  ) : (
                    <div className="space-y-2">
                      {svcResults.map((s) => (
                        <button
                          key={s.slug}
                          data-testid={`find-result-service-${s.slug}`}
                          onClick={() => goService(s.slug)}
                          className="w-full flex items-center justify-between gap-4 bg-white border border-kiki-line hover:border-kiki-gold/40 rounded-xl px-5 py-4 text-left transition-colors duration-300 group"
                        >
                          <span>
                            <span className="block font-display text-xl text-kiki-ink">{s.title}</span>
                            <span className="block text-sm text-kiki-muted font-light mt-0.5">{s.line}</span>
                          </span>
                          <ArrowUpRight className="w-4 h-4 shrink-0 text-kiki-muted group-hover:text-kiki-gold transition-colors duration-300" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-mono text-kiki-ink font-semibold mb-4">
                    <ShoppingBag className="w-3.5 h-3.5 text-kiki-gold" /> Digital Store
                  </p>
                  {prodResults.length === 0 ? (
                    <p className="text-sm text-kiki-muted font-light">No matching products.</p>
                  ) : (
                    <div className="space-y-2">
                      {prodResults.map((p) => (
                        <button
                          key={p.name}
                          data-testid={`find-result-product-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                          onClick={goStore}
                          className="w-full flex items-center justify-between gap-4 bg-white border border-kiki-line hover:border-kiki-gold/40 rounded-xl px-5 py-4 text-left transition-colors duration-300 group"
                        >
                          <span>
                            <span className="block font-display text-xl text-kiki-ink">{p.name}</span>
                            <span className="block text-sm text-kiki-gold font-outfit font-semibold mt-0.5">{p.price}</span>
                          </span>
                          <ArrowUpRight className="w-4 h-4 shrink-0 text-kiki-muted group-hover:text-kiki-gold transition-colors duration-300" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <p className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-mono text-kiki-ink font-semibold mb-4">
                    <PenLine className="w-3.5 h-3.5 text-kiki-gold" /> Blog — HRM Express
                    {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-kiki-muted" />}
                  </p>
                  {posts.length === 0 && !loading ? (
                    <p className="text-sm text-kiki-muted font-light">
                      No matching posts — <a href={BLOG_URL} target="_blank" rel="noopener noreferrer" className="text-kiki-gold underline">browse the blog</a>.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {posts.map((post, i) => (
                        <a
                          key={post.link}
                          data-testid={`find-result-blog-${i}`}
                          href={post.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between gap-4 bg-white border border-kiki-line hover:border-kiki-gold/40 rounded-xl px-5 py-4 transition-colors duration-300 group"
                        >
                          <span className="text-sm sm:text-base text-kiki-ink font-light">{decode(post.title.rendered)}</span>
                          <ArrowUpRight className="w-4 h-4 shrink-0 text-kiki-muted group-hover:text-kiki-gold transition-colors duration-300" />
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FindService;
